import React, { ChangeEvent } from 'react';

import { Input, Label, Radio, RadioGroup, Select, TypeSelectOption } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { useProductModalOptions } from '~/hooks/product';
import { useProductStore, useUserInfoStore } from '~/store';

import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductDetailsModalProductInfoSectionDefault.module.scss';

const ProductDetailsModalProductInfoSectionDefault = ({ hasTempData }: { hasTempData: boolean }) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const productModalOptions = useProductModalOptions();

  const [
    productDetailModalValue,
    setProductDetailModalValue,
    productModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    productModalSkuCodeStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productDetailModalValue,
      state.setProductDetailModalValue,
      state.productModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.productModalSkuCodeStatus,
      state.setFocusFieldRefs,
    ])
  );

  const handleClickRequiredInput = (e: React.MouseEvent) => {
    resetProductModalRequiredValueStatus(e.currentTarget.id as keyof TypeProductModalRequiredInputState);
  };

  const handleChangeProductNameInput = (e: ChangeEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      skuName: e.target.value,
    });

    resetProductModalRequiredValueStatus('skuName');
  };

  const handleClickCombineRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      combinedPackingAvailability: e.target.value as 'Y' | 'N',
    });
  };

  const handleBlurInput = (e: React.FocusEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      [e.currentTarget.id]: e.target.value.trim(),
    });
  };

  const handleChangeUseMethodCodeSelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      useMethodCode: value.value,
    });
  };

  return (
    <>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상품코드" required />
        <div className={styles['sku-code-input-wrapper']}>
          <Input
            ref={(el) => setFocusFieldRefs('skuCode', el)}
            id="skuCode"
            disabled
            className={styles['input']}
            type="text"
            size="small"
            name="skuCode"
            status={productModalSkuCodeStatus.status}
            validationMessage={productModalSkuCodeStatus.validationMessage}
            value={productDetailModalValue.skuCode}
          />
        </div>
      </div>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상품명" required />
        <div className={styles['sku-name-input-wrapper']}>
          <Input
            ref={(el) => setFocusFieldRefs('skuName', el)}
            id="skuName"
            disabled={hasPermission3PL || hasTempData}
            className={styles['input']}
            type="text"
            size="small"
            name="skuName"
            value={productDetailModalValue.skuName}
            count
            maxLength={40}
            placeholder="입력"
            status={productModalRequiredValueStatus.skuName.status}
            validationMessage={productModalRequiredValueStatus.skuName.message}
            onClick={handleClickRequiredInput}
            onChange={handleChangeProductNameInput}
            onBlur={handleBlurInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상온냉장 합포 여부" />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="combinedPackingType"
            value={productDetailModalValue.combinedPackingAvailability}
            size="medium"
            onChange={handleClickCombineRadio}
            disabled={hasPermissionFBK || hasTempData}
          >
            <Radio value="Y" label="예" />
            <Radio value="N" label="아니오" />
          </RadioGroup>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상품 사용여부" required={hasPermission3PL} />
        <div className={styles['input-wrapper']}>
          <Select
            id="useMethodCode"
            disabled={hasTempData}
            options={productModalOptions?.productUsageStatus ?? []}
            size="small"
            status={productModalRequiredValueStatus.useMethodCode?.status}
            validationMessage={productModalRequiredValueStatus.useMethodCode?.message}
            value={productModalOptions?.productUsageStatus.find(
              ({ value }) => value === productDetailModalValue.useMethodCode
            )}
            onChange={handleChangeUseMethodCodeSelect}
          />
        </div>
      </div>
    </>
  );
};

export default ProductDetailsModalProductInfoSectionDefault;
